import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import AudioPlayer from './AudioPlayer';
import StrokeAnimation from './StrokeAnimation';

/**
 * FlashcardBack — Inkwell editorial dictionary view.
 * Header hanzi rendered via StrokeAnimation (same SVG strokes as the front)
 * so the two faces share the exact same character rendering. Example
 * sentences stay as text (using --font-han) since they're paragraph-length
 * and need to be selectable.
 */
function FlashcardBack({ word }) {
  // Measure the header column width so the StrokeAnimation can size each
  // glyph to fit alongside the image + audio button.
  const headerColRef = useRef(null);
  const [colWidth, setColWidth] = useState(0);
  useLayoutEffect(() => {
    if (!headerColRef.current) return;
    const measure = () => {
      setColWidth(headerColRef.current?.clientWidth ?? 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(headerColRef.current);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const onResize = () => {
      if (headerColRef.current) setColWidth(headerColRef.current.clientWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!word) return null;
  const { hanzi, pinyin, audio, image, meanings = [] } = word;
  const len = (hanzi || '').length;

  // Desktop ceiling per-character — same tier as before, just used as a max.
  const desktopMax =
    len >= 8 ? 28 :
    len >= 6 ? 36 :
    len >= 5 ? 44 :
    len >= 4 ? 56 :
    len >= 3 ? 68 :
    len >= 2 ? 80 :
    88;
  // Compute per-char stroke size: divide measured column width by char count
  // (with a small gap budget), cap at desktop max, floor at a readable size.
  const sizeFromBudget = colWidth > 0
    ? Math.floor(colWidth / Math.max(len, 1)) - 6
    : desktopMax;
  const headerStrokeSize = Math.max(32, Math.min(sizeFromBudget, desktopMax));

  return (
    <div
      className="flashcard-back"
      style={{
        minHeight: 480,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header — hanzi top row with audio anchored to the right; pinyin
          stacks underneath so long idioms (e.g. xiōng yǒu chéng zhú) don't
          wrap into the hanzi column. */}
      <div
        style={{
          padding: 'clamp(20px, 5vw, 32px) clamp(16px, 4vw, 40px)',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(12px, 2vw, 24px)',
          }}
        >
          <div
            ref={headerColRef}
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <StrokeAnimation
              character={hanzi}
              autoAnimate={false}
              interactive={false}
              size={headerStrokeSize}
              strokeColor="#14110d"
              outlineColor="#d9d3c4"
              className="back-stroke-animation"
            />
          </div>
          {image && (
            <div
              style={{
                width: 'clamp(64px, 18vw, 96px)',
                height: 'clamp(64px, 18vw, 96px)',
                borderRadius: 'var(--r3)',
                overflow: 'hidden',
                border: '1px solid var(--rule)',
                flexShrink: 0,
              }}
            >
              <img
                src={image}
                alt={hanzi}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.parentElement.style.display = 'none';
                }}
              />
            </div>
          )}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <AudioPlayer src={audio} text={hanzi} className="header-audio" />
          </div>
        </div>
        {pinyin && (
          <div className="pinyin" style={{ fontSize: 'clamp(16px, 4vw, 22px)' }}>
            {pinyin}
          </div>
        )}
      </div>

      {/* Meanings + examples — let the card grow to fit content. Capped only
          on tall content via scroll on desktop; on mobile the page itself
          scrolls so the content is fully reachable. */}
      <div style={{ padding: 'clamp(20px, 5vw, 28px) clamp(16px, 4vw, 40px)' }}>
        {meanings.map((meaning, i) => (
          <div key={i} style={{ marginBottom: 20 }}>
            <div className="t-eyebrow" style={{ marginBottom: 12 }}>
              Meaning {String(i + 1).padStart(2, '0')}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 12,
                marginBottom: meaning.examples?.length > 0 ? 20 : 0,
              }}
            >
              {meaning.partOfSpeech && (
                <span
                  className="badge badge--accent"
                  style={{
                    fontFamily: 'var(--font-mono)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {meaning.partOfSpeech}
                </span>
              )}
              <span style={{ fontSize: 18, color: 'var(--ink)' }}>{meaning.definition}</span>
            </div>

            {meaning.examples && meaning.examples.length > 0 && (
              <div>
                <div className="t-eyebrow" style={{ marginBottom: 12 }}>
                  Examples
                </div>
                {meaning.examples.map((ex, j) => (
                  <div
                    key={j}
                    style={{
                      paddingBottom: 16,
                      marginBottom: 16,
                      borderBottom: '1px solid var(--rule-2)',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 4,
                      }}
                    >
                      <span
                        className="t-mono"
                        style={{ fontSize: 11, color: 'var(--ink-3)' }}
                      >
                        {String(j + 1).padStart(2, '0')}
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--font-han)',
                          fontSize: 22,
                          color: 'var(--ink)',
                        }}
                      >
                        {ex.cn}
                      </span>
                      <div onClick={(e) => e.stopPropagation()}>
                        <AudioPlayer src={ex.audio} text={ex.cn} className="example-audio" />
                      </div>
                    </div>
                    {ex.pinyin && (
                      <div
                        className="pinyin"
                        style={{ marginLeft: 28, marginBottom: 2, fontSize: 14 }}
                      >
                        {ex.pinyin}
                      </div>
                    )}
                    <div className="t-small" style={{ marginLeft: 28 }}>
                      {ex.en}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default FlashcardBack;
